export interface RadioactiveMaterial {
    id: string;
    name: string;
    category: string;
    radiationType: string[];
    halfLife: string;
    halfLifeYears: number;
    usages: string[];
    dangerLevel: number; // 1-5 척도
    baseActivityBq: number; // 베크렐 단위의 기본 방사능
    atomicNumber: number;
    massNumber: number;
    gammaConstant: number;
    physicalForm: string; // 물리적 형태 추가 (고체, 액체, 기체 등)
}

export const radioactiveMaterials: RadioactiveMaterial[] = [
    {
        "id": "am-241",
        "name": "아메리슘-241",
        "category": "산업용",
        "radiationType": ["알파선", "감마선"],
        "halfLife": "432.2년",
        "halfLifeYears": 432.2,
        "usages": ["연기 감지기", "산업용 측정기"],
        "dangerLevel": 4,
        "baseActivityBq": 1,
        "atomicNumber": 95,
        "massNumber": 241,
        "gammaConstant": 0.005,
        "physicalForm": "고체 (산화물 형태)"
    },
    {
        "id": "ba-133",
        "name": "바륨-133",
        "category": "의료 및 연구용",
        "radiationType": ["감마선"],
        "halfLife": "10.5년",
        "halfLifeYears": 10.5,
        "usages": ["감마선 검출기 교정", "핵의학 연구"],
        "dangerLevel": 2,
        "baseActivityBq": 1,
        "atomicNumber": 56,
        "massNumber": 133,
        "gammaConstant": 0.23,
        "physicalForm": "고체 (밀봉형)"
    },
    {
        "id": "co-57",
        "name": "코발트-57",
        "category": "의료 및 산업용",
        "radiationType": ["감마선"],
        "halfLife": "271.8일",
        "halfLifeYears": 271.8 / 365.25,
        "usages": ["SPECT 스캔", "측정기기 교정"],
        "dangerLevel": 2,
        "baseActivityBq": 1,
        "atomicNumber": 27,
        "massNumber": 57,
        "gammaConstant": 0.36,
        "physicalForm": "고체 (소형 금속)"
    },
    {
        "id": "co-60",
        "name": "코발트-60",
        "category": "의료 및 산업용",
        "radiationType": ["감마선", "베타선"],
        "halfLife": "5.27년",
        "halfLifeYears": 5.27,
        "usages": ["방사선 치료", "식품 조사", "산업용 비파괴검사"],
        "dangerLevel": 5,
        "baseActivityBq": 1,
        "atomicNumber": 27,
        "massNumber": 60,
        "gammaConstant": 0.35,
        "physicalForm": "고체 (봉입형)"
    },
    {
        "id": "cs-137",
        "name": "세슘-137",
        "category": "의료 및 산업용",
        "radiationType": ["감마선", "베타선"],
        "halfLife": "30.1년",
        "halfLifeYears": 30.1,
        "usages": ["방사선 치료", "산업용 레벨 게이지"],
        "dangerLevel": 5,
        "baseActivityBq": 1,
        "atomicNumber": 55,
        "massNumber": 137,
        "gammaConstant": 0.089,
        "physicalForm": "고체 (염 형태 봉입)"
    },
    {
        "id": "cf-252",
        "name": "캘리포늄-252",
        "category": "산업 및 군사용",
        "radiationType": ["중성자선", "감마선"],
        "halfLife": "2.645년",
        "halfLifeYears": 2.645,
        "usages": ["중성자 조사", "핵연료 검사", "폭발물 탐지"],
        "dangerLevel": 5,
        "baseActivityBq": 1,
        "atomicNumber": 98,
        "massNumber": 252,
        "gammaConstant": 2.31,
        "physicalForm": "고체 (산화물/금속 캡슐형)"
    },
    {
        "id": "kr-85",
        "name": "크립톤-85",
        "category": "산업용",
        "radiationType": ["베타선", "감마선"],
        "halfLife": "10.76년",
        "halfLifeYears": 10.76,
        "usages": ["형광등 충전가스", "방사선 측정기"],
        "dangerLevel": 3,
        "baseActivityBq": 1,
        "atomicNumber": 36,
        "massNumber": 85,
        "gammaConstant": 0.017,
        "physicalForm": "기체"
    },
    {
        "id": "ni-63",
        "name": "니켈-63",
        "category": "산업용",
        "radiationType": ["베타선"],
        "halfLife": "100.1년",
        "halfLifeYears": 100.1,
        "usages": ["베타선 전자 소스", "전자기기 정전기 방지"],
        "dangerLevel": 2,
        "baseActivityBq": 1,
        "atomicNumber": 28,
        "massNumber": 63,
        "gammaConstant": 0.0,
        "physicalForm": "고체 (박막형)"
    },
    {
        "id": "sr-90",
        "name": "스트론튬-90",
        "category": "의료 및 군사 잔존 방사능",
        "radiationType": ["베타선"],
        "halfLife": "28.8년",
        "halfLifeYears": 28.8,
        "usages": ["열전지(RTG)", "핵실험 잔여물 분석"],
        "dangerLevel": 5,
        "baseActivityBq": 1,
        "atomicNumber": 38,
        "massNumber": 90,
        "gammaConstant": 0.0,
        "physicalForm": "고체 (산화물 봉입)"
    }
];

// 방사선량 계산 함수 (거리에 따른 시버트 단위)
export const calculateDose = (
    baseActivityBq: number, 
    distance: number, // 미터 단위
    time: number, // 시간 단위
    gammaConstant: number // 감마 상수 추가
): number => {
    
    // 거리 제곱 법칙 적용
    // μSv/h = (Γ × A) / d²
    // A는 방사능(MBq), d는 거리(m)
    const doseRate = (gammaConstant * (baseActivityBq / 1e6)) / (distance * distance);
    
    // 시간을 곱해서 총 피폭량 계산
    return doseRate * time;
};

export interface WindInfo {
    풍속: string;
    풍향: string;
}

export interface DataItem {
    id: number;
    물질: string;
    물질량: string;
    주소: string;
    위치이미지: string;
    바람정보: WindInfo;
} 